/**
 * Card data for the homepage Customers Slider (Figma 6387:57385 / 6037:29003).
 * Placeholder story hrefs until case-study wiring lands.
 */

import {
  getCustomerGridBrandsForLocale,
  getLocaleGeographies,
} from "@/components/blocks/customers-grid-data";
import type {
  CustomerGeography,
  LocalizedCustomerGridBrand,
} from "@/components/blocks/customers-grid-data";

export type Company = string;

interface CardBase {
  /** Placeholder case-study destination for the expanded CTA. */
  storyHref: string;
}

export interface FounderCardData extends CardBase {
  kind: "founder";
  company: Company;
  name: string;
  role: string;
  quote: string;
  /** Placeholder expanded copy — longer body shown in the spotlight state. */
  expandedQuote: string;
  avatarSrc: string;
  /** object-position for the 64px avatar crop. */
  avatarPosition: string;
}

export interface TestimonialCardData extends CardBase {
  kind: "testimonial";
  quote: string;
  expandedQuote: string;
  company: Company;
}

export interface ResultsCardData extends CardBase {
  kind: "results";
  variant: "dark" | "blue" | "white";
  company: Company;
  stat: string;
  label: string;
  /** Placeholder supporting line for the expanded spotlight state. */
  expandedDetail: string;
}

export interface VideoCardData {
  kind: "video";
  company: Company;
  playbackId: string;
  posterSrc: string;
  alt: string;
  title: string;
}

function muxThumbnailUrl(playbackId: string, time = 1) {
  return `https://image.mux.com/${playbackId}/thumbnail.webp?fit_mode=smartcrop&time=${time}`;
}

export type CardData =
  | FounderCardData
  | TestimonialCardData
  | ResultsCardData
  | VideoCardData;

const WORDY_QUOTE_LENGTH = 120;

export function getCustomerQuotePreview(
  quote: string,
  summary?: string
): {
  text: string;
  isWordy: boolean;
} {
  const normalizedQuote = quote.replaceAll(/\s+/g, " ").trim();
  if (normalizedQuote.length <= WORDY_QUOTE_LENGTH) {
    return { text: normalizedQuote, isWordy: false };
  }

  return {
    text: summary ?? normalizedQuote,
    isWordy: true,
  };
}

export function shouldPauseCustomerMarquee(
  videoPaused: boolean,
  expandedCardId: string | null
): boolean {
  return videoPaused || expandedCardId !== null;
}

type CustomerCopyLanguage = "en" | "de" | "es" | "nl";

const CUSTOMER_QUOTE_SUMMARIES: Record<
  string,
  Record<CustomerCopyLanguage, string>
> = {
  "3bears": {
    en: "Wayflyer understands how growing businesses work.",
    de: "Wayflyer überzeugte mit Flexibilität und einem transparenten Prozess.",
    es: "Wayflyer entiende cómo funcionan las empresas en crecimiento.",
    nl: "Wayflyer begrijpt hoe groeiende bedrijven werken.",
  },
  "act-and-acre": {
    en: "Wayflyer is a strategic growth partner for founders.",
    de: "Wayflyer hilft Gründern, strategisch zu wachsen.",
    es: "Wayflyer ayuda a los fundadores a crecer de forma estratégica.",
    nl: "Wayflyer helpt oprichters strategisch te groeien.",
  },
  "cole-buxton": {
    en: "Wayflyer helped Cole Buxton grow while remaining founder-owned.",
    de: "Mit Wayflyer konnte Cole Buxton wachsen und in Gründerhand bleiben.",
    es: "Wayflyer ayudó a Cole Buxton a crecer sin dejar de ser propiedad de sus fundadores.",
    nl: "Met Wayflyer kon Cole Buxton groeien en in handen van de oprichters blijven.",
  },
  correxiko: {
    en: "Wayflyer understood Correxiko and offered the right funding quickly.",
    de: "Wayflyer verstand Correxiko und stellte schnell die passende Finanzierung bereit.",
    es: "Wayflyer entendió a Correxiko y le ofreció rápidamente la financiación adecuada.",
    nl: "Wayflyer begreep Correxiko en bood snel de juiste financiering.",
  },
  frive: {
    en: "Wayflyer made flexible financing straightforward and efficient for Frive.",
    de: "Wayflyer machte flexible Finanzierung für Frive einfach und effizient.",
    es: "Wayflyer hizo que la financiación flexible de Frive fuera sencilla y eficiente.",
    nl: "Wayflyer maakte flexibele financiering voor Frive eenvoudig en efficiënt.",
  },
  "get-swim-go": {
    en: "Wayflyer provided financing exactly when Get Swim Go needed it.",
    de: "Wayflyer stellte die Finanzierung genau dann bereit, als Get Swim Go sie brauchte.",
    es: "Wayflyer proporcionó financiación justo cuando Get Swim Go la necesitaba.",
    nl: "Wayflyer bood financiering precies toen Get Swim Go die nodig had.",
  },
  "ghost-golf": {
    en: "Wayflyer understood Ghost Golf’s inventory and marketing strategy.",
    de: "Wayflyer verstand die Bestands- und Marketingstrategie von Ghost Golf.",
    es: "Wayflyer entendió la estrategia de inventario y marketing de Ghost Golf.",
    nl: "Wayflyer begreep de voorraad- en marketingstrategie van Ghost Golf.",
  },
  heights: {
    en: "Wayflyer supported Heights with reliable financing for growth.",
    de: "Wayflyer unterstützte das Wachstum von Heights mit zuverlässiger Finanzierung.",
    es: "Wayflyer apoyó el crecimiento de Heights con financiación fiable.",
    nl: "Wayflyer ondersteunde de groei van Heights met betrouwbare financiering.",
  },
  "kekoa-foods": {
    en: "Wayflyer helped Kekoa Foods bridge its inventory funding gap.",
    de: "Wayflyer half Kekoa Foods, die Finanzierungslücke beim Warenbestand zu schließen.",
    es: "Wayflyer ayudó a Kekoa Foods a cubrir la financiación de su inventario.",
    nl: "Wayflyer hielp Kekoa Foods de financiering van de voorraad rond te krijgen.",
  },
  laagam: {
    en: "Wayflyer combines fast decisions, industry knowledge and ongoing support.",
    de: "Wayflyer verbindet schnelle Entscheidungen, Branchenkenntnis und laufende Unterstützung.",
    es: "Wayflyer combina decisiones rápidas, conocimiento del sector y apoyo continuo.",
    nl: "Wayflyer combineert snelle beslissingen, sectorkennis en doorlopende ondersteuning.",
  },
  "nana-joes-granola": {
    en: "Wayflyer made financing quick and straightforward for Nana Joes Granola.",
    de: "Wayflyer machte die Finanzierung für Nana Joes Granola schnell und unkompliziert.",
    es: "Wayflyer hizo que la financiación de Nana Joes Granola fuera rápida y sencilla.",
    nl: "Wayflyer maakte financiering voor Nana Joes Granola snel en eenvoudig.",
  },
  "nutri-bites": {
    en: "Wayflyer gives Nutri Bites added financial strength when opportunities arise.",
    de: "Wayflyer gibt Nutri Bites zusätzliche finanzielle Stärke für neue Chancen.",
    es: "Wayflyer aporta a Nutri Bites más solidez financiera ante nuevas oportunidades.",
    nl: "Wayflyer geeft Nutri Bites extra financiële slagkracht voor nieuwe kansen.",
  },
  spongelle: {
    en: "Wayflyer made funding fast, efficient, and easy for Spongellé.",
    de: "Wayflyer machte die Finanzierung für Spongellé schnell, effizient und einfach.",
    es: "Wayflyer hizo que la financiación de Spongellé fuera rápida, eficiente y sencilla.",
    nl: "Wayflyer maakte financiering voor Spongellé snel, efficiënt en eenvoudig.",
  },
  "tallow-naturals": {
    en: "Wayflyer made funding fast and uncomplicated for Tallow Naturals.",
    de: "Wayflyer machte die Finanzierung für Tallow Naturals schnell und unkompliziert.",
    es: "Wayflyer hizo que la financiación de Tallow Naturals fuera rápida y sencilla.",
    nl: "Wayflyer maakte financiering voor Tallow Naturals snel en eenvoudig.",
  },
  "sierra-madre-golf": {
    en: "Wayflyer helped Sierra Madre Golf scale inventory, marketing and its team.",
    de: "Wayflyer half Sierra Madre Golf, Bestand, Marketing und Team zu skalieren.",
    es: "Wayflyer ayudó a Sierra Madre Golf a ampliar su inventario, marketing y equipo.",
    nl: "Wayflyer hielp Sierra Madre Golf om voorraad, marketing en team op te schalen.",
  },
  "sand-cloud": {
    en: "Wayflyer gives Sand Cloud personal financing support built for eCommerce.",
    de: "Wayflyer bietet Sand Cloud persönliche Finanzierung für den eCommerce.",
    es: "Wayflyer ofrece a Sand Cloud financiación personalizada para eCommerce.",
    nl: "Wayflyer biedt Sand Cloud persoonlijke financiering voor eCommerce.",
  },
  ta3: {
    en: "Wayflyer made inventory financing simple for TA3.",
    de: "Wayflyer machte die Bestandsfinanzierung für TA3 einfach.",
    es: "Wayflyer simplificó la financiación de inventario para TA3.",
    nl: "Wayflyer maakte voorraadfinanciering eenvoudig voor TA3.",
  },
  "true-classic": {
    en: "Wayflyer funds future growth, not just proven history.",
    de: "Wayflyer finanziert zukünftiges Wachstum, nicht nur bisherige Erfolge.",
    es: "Wayflyer financia el crecimiento futuro, no solo los resultados anteriores.",
    nl: "Wayflyer financiert toekomstige groei, niet alleen eerdere resultaten.",
  },
  valkental: {
    en: "Wayflyer helped Valkental finance a large production run.",
    de: "Wayflyer half Valkental, eine große Produktion zu finanzieren.",
    es: "Wayflyer ayudó a Valkental a financiar una gran producción.",
    nl: "Wayflyer hielp Valkental een grote productierun te financieren.",
  },
  "wednesdays-domain": {
    en: "Wayflyer helps Wednesdays Domain finance inventory while protecting cashflow.",
    de: "Wayflyer hilft Wednesdays Domain, Bestand zu finanzieren und den Cashflow zu schützen.",
    es: "Wayflyer ayuda a Wednesdays Domain a financiar inventario y proteger su cashflow.",
    nl: "Wayflyer helpt Wednesdays Domain voorraad te financieren en de cashflow te beschermen.",
  },
};

function customerCopyLanguage(locale: string): CustomerCopyLanguage {
  const language = locale.split("-")[0];
  return language === "de" || language === "es" || language === "nl"
    ? language
    : "en";
}

function getCustomerQuoteSummary(
  customerId: string,
  locale: string
): string | undefined {
  const summaries = CUSTOMER_QUOTE_SUMMARIES[customerId];
  if (!summaries) return undefined;

  return summaries[customerCopyLanguage(locale)];
}

/**
 * Slider-specific testimonial copy that swaps the roles of the card faces:
 * the visible card carries the (edited) full quote and the hover spotlight
 * carries the short summary line.
 */
const CUSTOMER_TESTIMONIAL_COPY_OVERRIDES: Record<
  string,
  Partial<
    Record<CustomerCopyLanguage, { quote?: string; expandedQuote?: string }>
  >
> = {
  "3bears": {
    en: {
      quote:
        "It's good to have a partner like Wayflyer who understands how a business works, what it needs, and believes in our vision",
      expandedQuote:
        "Now Germany’s number one breakfast brand, with a Harry Kane cereal line on shelves and Wayflyer behind the retail rollouts.",
    },
  },
  "act-and-acre": {
    en: {
      quote:
        "Wayflyer isn't just a finance provider they're a strategic growth partner for founders looking to scale quickly while maintaining ownership. Wayflyer is a gamechanger.",
      expandedQuote:
        "When Sephora came calling, they had to fund inventory for 235 stores nationwide before a single sale. Wayflyer made the launch possible.",
    },
  },
  "cole-buxton": {
    en: {
      quote:
        "We’ve scaled it up to a great place while still privately owning the company 100 percent. Wayflyer has allowed us to concentrate on keeping the business how we want it.",
      expandedQuote:
        "Wayflyer helped Cole Buxton grow while remaining founder-owned.",
    },
  },
  "get-swim-go": {
    en: {
      quote:
        "Wayflyer stepped in with a short-term facility so the builds could start on plan. The business now has two more profitable sites up and running.",
      expandedQuote:
        "Wayflyer provided financing exactly when Get Swim Go needed it.",
    },
  },
  heights: {
    en: {
      quote:
        "Wayflyer were there for us when we needed it and always reliable and helpful in our pursuit of growth.",
      expandedQuote:
        "Wayflyer supported Heights with reliable financing for growth.",
    },
  },
  laagam: {
    en: {
      quote:
        "Wayflyer combines fast decisions, a strong understanding of our industry, and great ongoing support. We would highly recommend them.",
      expandedQuote:
        "Laagam produces on demand, making only what customers have already bought. A model that lean needs funding that moves at the same speed.",
    },
  },
  lick: {
    en: {
      expandedQuote:
        "They raised venture to launch. Now they’ve scaled from DTC to multi-channel without giving away further equity.",
    },
  },
  "nana-joes-granola": {
    en: {
      quote:
        "We applied and got the money pretty quickly. But the team really wanted to get to know me, my business model, and what the money was for.",
      expandedQuote:
        "Pandemic pricing pushed her oats from 93 cents to $4 a pound. Wayflyer funded truckload-sized orders that pulled costs back down and kept every blend on the shelf.",
    },
  },
  ta3: {
    en: {
      quote:
        "Before Wayflyer we had a venture debt deal and it was very stressful. The second we switched over, everything was easy.",
      expandedQuote:
        "Venture debt came with pressure and restrictions. Wayflyer funds the inventory instead, so stock arrives ahead of each season without the stress attached.",
    },
  },
  "tallow-naturals": {
    en: {
      quote:
        "Working with Wayflyer was fast and completely uncomplicated, from application to funding took only a few days, with none of the friction we're used to from traditional lenders.",
      expandedQuote:
        "For a fast-growing eCommerce business, that speed and flexibility isn’t a nice-to-have. It’s what keeps us scaling.",
    },
  },
  valkental: {
    en: {
      quote:
        "Wayflyer helped us finance a very large production run, which contributed to a very successful year. That was a huge help.",
      expandedQuote:
        "Lead times of four to seven months, 30% paid upfront, then months of import before anything sells. Wayflyer funds the gap between paying and earning.",
    },
  },
};

/**
 * Slider-specific results copy. The grid keeps its approved Shorter Card
 * wording; only the slider tile and its hover spotlight change here.
 */
const CUSTOMER_RESULTS_COPY_OVERRIDES: Record<
  string,
  Partial<
    Record<CustomerCopyLanguage, { label?: string; expandedDetail?: string }>
  >
> = {
  brez: {
    en: {
      expandedDetail:
        "Growing this fast in beverage means producing months ahead of every demand spike. Wayflyer funds the production runs so supply never has to slow the brand down.",
    },
  },
  "little-words-project": {
    en: {
      expandedDetail:
        "From 15 stores toward a 100-door retail push, backed by a funder who looked at a bootstrapped jewelry brand and saw the potential.",
    },
  },
  myomaster: {
    en: {
      expandedDetail:
        "The investors who said no are calling back, yet MyoMaster still chooses Wayflyer.",
    },
  },
  perfectted: {
    en: {
      expandedDetail:
        "From a Dragon’s Den deal to the UK’s fastest-growing matcha brand, with Wayflyer funding the massive opportunities along the way.",
    },
  },
  sons: {
    en: {
      expandedDetail:
        "Grown between equity rounds without giving up a single share, then into the next raise at a higher valuation.",
    },
  },
};

/**
 * Verbatim customer quotes render inside typographic quotation marks. Source
 * quotes mix straight and curly apostrophes, so normalise the between-letter
 * ones here — a blanket replace would break nested 'single quoted' asides.
 */
function withQuotationMarks(text: string): string {
  return `“${text.replaceAll(/(\p{L})'(\p{L})/gu, "$1’$2")}”`;
}

const CUSTOMER_SLIDER_UI_COPY: Record<
  CustomerCopyLanguage,
  {
    headingMobile: string;
    headingDesktop: (count: string) => string;
    ctaLabel: string;
    /** Used when the card has no case study of its own to link to. */
    ctaIndexLabel: string;
  }
> = {
  en: {
    headingMobile: "Trusted by thousands of the world’s best businesses",
    headingDesktop: (count) =>
      `Trusted by ${count}+ of the world’s best businesses`,
    ctaLabel: "Read full story",
    ctaIndexLabel: "See more stories",
  },
  de: {
    headingMobile: "Tausende der besten Unternehmen weltweit vertrauen uns.",
    headingDesktop: (count) =>
      `Mehr als ${count} der besten Unternehmen weltweit vertrauen uns.`,
    ctaLabel: "Erfolgsgeschichte lesen",
    ctaIndexLabel: "Weitere Erfolgsgeschichten",
  },
  es: {
    headingMobile:
      "Miles de las mejores empresas del mundo confían en nosotros.",
    headingDesktop: (count) =>
      `Más de ${count} de las mejores empresas del mundo confían en nosotros.`,
    ctaLabel: "Historia completa",
    ctaIndexLabel: "Ver más historias",
  },
  nl: {
    headingMobile:
      "Duizenden van de beste bedrijven ter wereld vertrouwen ons.",
    headingDesktop: (count) =>
      `Meer dan ${count} van de beste bedrijven ter wereld vertrouwen ons.`,
    ctaLabel: "Lees het verhaal",
    ctaIndexLabel: "Meer verhalen bekijken",
  },
};

/** Brands with no case study of their own point at the customers listing. */
export const CUSTOMERS_INDEX_HREF = "/our-customers";

export function getCustomerSliderUiCopy(locale: string, count: number) {
  const copy = CUSTOMER_SLIDER_UI_COPY[customerCopyLanguage(locale)];
  return {
    headingMobile: copy.headingMobile,
    headingDesktop: copy.headingDesktop(count.toLocaleString(locale)),
    ctaLabel: copy.ctaLabel,
    ctaIndexLabel: copy.ctaIndexLabel,
  };
}

const CUSTOMER_RESULT_COPY: Record<
  CustomerCopyLanguage,
  Record<string, string>
> = {
  en: {},
  de: {
    "New points of sale": "Neue Verkaufsstellen",
    "Founder owned, because they never needed to give away equity":
      "In Gründerhand, weil sie nie Anteile abgeben mussten",
    "Bigger offer received than any other lender":
      "Größeres Angebot als bei allen anderen Anbietern",
    "Revenue, still founder-led": "Umsatz, weiterhin gründergeführt",
    "Profitable new sites owned": "Profitable neue Standorte im Eigentum",
    "Investors said no, Wayflyer funded the potential":
      "Investoren sagten Nein, Wayflyer finanzierte das Potenzial",
    "Stores at launch": "Filialen zum Start",
    "Revenue growth with Wayflyer": "Umsatzwachstum mit Wayflyer",
    "Sephora stores nationwide": "Sephora-Filialen im ganzen Land",
    "Revenue in two years": "Umsatz in 2 Jahren",
    "In unmet demand before Wayflyer": "An unerfüllter Nachfrage vor Wayflyer",
    "Stores in one year": "Filialen in 1 Jahr",
    "Retail store expansion underway": "Einzelhandelsexpansion läuft",
    "Margin increase": "Margensteigerung",
    "Annual revenue, zero equity given up.":
      "Jahresumsatz, ohne abgegebene Anteile.",
    "Year-on-year growth": "Wachstum gegenüber dem Vorjahr",
    "From application to money in the bank.":
      "Von der Antragstellung bis zum Geld auf dem Konto.",
    "Additional revenue tied to Wayflyer": "Zusätzlicher Umsatz dank Wayflyer",
  },
  es: {
    "New points of sale": "Nuevos puntos de venta",
    "Founder owned, because they never needed to give away equity":
      "En manos de sus fundadores, porque nunca tuvieron que ceder capital",
    "Bigger offer received than any other lender":
      "Una oferta mayor que la de cualquier otro proveedor",
    "Revenue, still founder-led":
      "Ingresos, aún bajo la dirección de sus fundadores",
    "Profitable new sites owned": "Nuevas ubicaciones rentables en propiedad",
    "Investors said no, Wayflyer funded the potential":
      "Los inversores dijeron no, Wayflyer financió el potencial",
    "Stores at launch": "Tiendas en el lanzamiento",
    "Revenue growth with Wayflyer": "Crecimiento de ingresos con Wayflyer",
    "Sephora stores nationwide": "Tiendas Sephora en todo el país",
    "Revenue in two years": "Ingresos en 2 años",
    "In unmet demand before Wayflyer":
      "En demanda no satisfecha antes de Wayflyer",
    "Stores in one year": "Tiendas en 1 año",
    "Retail store expansion underway": "Expansión de tiendas en marcha",
    "Margin increase": "Aumento del margen",
    "Annual revenue, zero equity given up.":
      "Ingresos anuales, sin ceder capital.",
    "Year-on-year growth": "Crecimiento interanual",
    "From application to money in the bank.":
      "De la solicitud al dinero en el banco.",
    "Additional revenue tied to Wayflyer":
      "Ingresos adicionales gracias a Wayflyer",
  },
  nl: {
    "New points of sale": "Nieuwe verkooppunten",
    "Founder owned, because they never needed to give away equity":
      "In handen van de oprichters, omdat ze nooit eigen vermogen hoefden af te staan",
    "Bigger offer received than any other lender":
      "Een groter aanbod dan bij andere aanbieders",
    "Revenue, still founder-led": "Omzet, nog steeds geleid door de oprichters",
    "Profitable new sites owned": "Nieuwe winstgevende locaties in eigendom",
    "Investors said no, Wayflyer funded the potential":
      "Investeerders zeiden nee, Wayflyer financierde het potentieel",
    "Stores at launch": "Winkels bij de lancering",
    "Revenue growth with Wayflyer": "Omzetgroei met Wayflyer",
    "Sephora stores nationwide": "Sephora-winkels in het hele land",
    "Revenue in two years": "Omzet in 2 jaar",
    "In unmet demand before Wayflyer": "Aan onvervulde vraag vóór Wayflyer",
    "Stores in one year": "Winkels in 1 jaar",
    "Retail store expansion underway": "Winkeluitbreiding is gaande",
    "Margin increase": "Margestijging",
    "Annual revenue, zero equity given up.":
      "Jaaromzet, zonder eigen vermogen af te staan.",
    "Year-on-year growth": "Jaar-op-jaargroei",
    "From application to money in the bank.":
      "Van aanvraag tot geld op de rekening.",
    "Additional revenue tied to Wayflyer": "Extra omzet dankzij Wayflyer",
  },
};

type PlannedCardKind = "testimonial" | "results" | "video";

const TOP_CARD_PATTERN: readonly PlannedCardKind[] = [
  "testimonial",
  "results",
  "video",
  "testimonial",
  "results",
  "video",
];
const BOTTOM_CARD_PATTERN: readonly PlannedCardKind[] = [
  "results",
  "video",
  "testimonial",
  "results",
  "video",
  "testimonial",
];
const RESULT_VARIANTS = ["dark", "blue", "white"] as const;

/**
 * Results cards pinned by locale: `replaces` is the brand the geography-ordered
 * selector would otherwise have put in that slot, `brandId` is the brand that
 * takes it instead.
 *
 * A pinned brand is skipped by normal results selection and enters only through
 * its pin, so it can never hold two results cards. It is deliberately *not*
 * marked used, so it can still hold a card of another kind — True Classic keeps
 * its `en` video card alongside the pinned tile.
 */
const PINNED_RESULTS_CARDS: Record<
  string,
  readonly { replaces: string; brandId: string }[]
> = {
  en: [{ replaces: "brez", brandId: "true-classic" }],
  "en-GB": [{ replaces: "perfectted", brandId: "true-classic" }],
  "en-IE": [{ replaces: "perfectted", brandId: "true-classic" }],
  "en-AU": [{ replaces: "perfectted", brandId: "true-classic" }],
  de: [{ replaces: "perfectted", brandId: "true-classic" }],
};

function supportsCardKind(
  brand: LocalizedCustomerGridBrand,
  kind: PlannedCardKind
): boolean {
  if (kind === "testimonial") return Boolean(brand.quote);
  if (kind === "results") return Boolean(brand.shortCard);
  return Boolean(brand.video);
}

function selectCustomerBrand(
  brands: LocalizedCustomerGridBrand[],
  usedBrandIds: Set<string>,
  kind: PlannedCardKind,
  localeGeographies: readonly CustomerGeography[],
  excludedBrandIds?: ReadonlySet<string>
): LocalizedCustomerGridBrand {
  const candidate = brands
    .map((brand, index) => ({ brand, index }))
    .filter(
      ({ brand }) =>
        !usedBrandIds.has(brand.id) &&
        !excludedBrandIds?.has(brand.id) &&
        supportsCardKind(brand, kind)
    )
    .toSorted((a, b) => {
      const geographyPriority =
        Number(localeGeographies.includes(b.brand.geography)) -
        Number(localeGeographies.includes(a.brand.geography));
      if (geographyPriority !== 0) return geographyPriority;

      if (kind !== "video") {
        const preserveVideoPriority =
          Number(Boolean(a.brand.video)) - Number(Boolean(b.brand.video));
        if (preserveVideoPriority !== 0) return preserveVideoPriority;
      }

      return a.index - b.index;
    })[0]?.brand;

  if (!candidate) {
    throw new Error(`Not enough unique customer data for ${kind} cards`);
  }

  usedBrandIds.add(candidate.id);
  return candidate;
}

function localizedVideoAlt(
  brandName: string,
  language: CustomerCopyLanguage
): string {
  if (language === "de")
    return `30-sekündige Kundengeschichte von ${brandName}`;
  if (language === "es")
    return `Historia de cliente de ${brandName} en 30 segundos`;
  if (language === "nl") return `Klantverhaal van ${brandName} in 30 seconden`;
  return `${brandName} customer story in 30 seconds`;
}

function createCustomerCard(
  brand: LocalizedCustomerGridBrand,
  kind: PlannedCardKind,
  locale: string,
  resultIndex: number
): CardData {
  const language = customerCopyLanguage(locale);

  if (kind === "testimonial") {
    const override = CUSTOMER_TESTIMONIAL_COPY_OVERRIDES[brand.id]?.[language];
    const summary = getCustomerQuoteSummary(brand.id, locale);
    // Most brand quotes are English-only, so on other locales prefer the
    // translated summary over showing the reader an English paragraph.
    const usableQuote = brand.quoteIsLocalized ? brand.quote : undefined;
    // Visible verbatim quotes get quotation marks; summary paraphrases don't.
    const quote = override?.quote
      ? withQuotationMarks(override.quote)
      : (summary ??
        withQuotationMarks(
          getCustomerQuotePreview(usableQuote ?? brand.quote!).text
        ));
    return {
      kind,
      company: brand.id,
      quote,
      expandedQuote:
        override?.expandedQuote ?? usableQuote ?? summary ?? brand.quote!,
      storyHref: brand.href,
    };
  }

  if (kind === "results") {
    const shortCard = brand.shortCard!;
    const override = CUSTOMER_RESULTS_COPY_OVERRIDES[brand.id]?.[language];
    // Both faces use the translated label — the raw one is English.
    const label =
      (shortCard.label
        ? CUSTOMER_RESULT_COPY[language][shortCard.label]
        : undefined) ?? shortCard.label;
    return {
      kind,
      variant: RESULT_VARIANTS[resultIndex % RESULT_VARIANTS.length]!,
      company: brand.id,
      stat: shortCard.stat,
      label: override?.label ?? label ?? "",
      expandedDetail:
        override?.expandedDetail ??
        getCustomerQuoteSummary(brand.id, locale) ??
        label ??
        shortCard.stat,
      storyHref: brand.href,
    };
  }

  return {
    kind,
    company: brand.id,
    playbackId: brand.video!.playbackId,
    posterSrc: muxThumbnailUrl(
      brand.video!.playbackId,
      brand.video!.posterTime
    ),
    alt: localizedVideoAlt(brand.name, language),
    title: brand.name,
  };
}

export function getCustomerSliderRows(locale: string): {
  top: CardData[];
  bottom: CardData[];
} {
  const brands = getCustomerGridBrandsForLocale(locale);
  const localeGeographies = getLocaleGeographies(locale);
  const usedBrandIds = new Set<string>();
  let resultIndex = 0;

  const pins = PINNED_RESULTS_CARDS[locale] ?? [];
  const pinnedBrandIds = new Set(pins.map((pin) => pin.brandId));

  const resolveResultsBrand = (selected: LocalizedCustomerGridBrand) => {
    const pin = pins.find(({ replaces }) => replaces === selected.id);
    const pinned = pin && brands.find(({ id }) => id === pin.brandId);
    return pinned?.shortCard ? pinned : selected;
  };

  const buildRow = (pattern: readonly PlannedCardKind[]) =>
    pattern.map((kind) => {
      const selected = selectCustomerBrand(
        brands,
        usedBrandIds,
        kind,
        localeGeographies,
        // Pinned brands enter results slots only through their pin.
        kind === "results" ? pinnedBrandIds : undefined
      );
      const brand =
        kind === "results" ? resolveResultsBrand(selected) : selected;
      const card = createCustomerCard(brand, kind, locale, resultIndex);
      if (kind === "results") resultIndex += 1;
      return card;
    });

  return {
    top: buildRow(TOP_CARD_PATTERN),
    bottom: buildRow(BOTTOM_CARD_PATTERN),
  };
}
